import React, { useState } from "react";
import {
  Chart as ChartJS,
  ArcElement,
  Tooltip as ChartTooltip,
  Legend,
} from "chart.js";
import { Pie } from "react-chartjs-2";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet";
import FAQSchema from "../../FAQSchema";

// Register necessary components
ChartJS.register(ArcElement, ChartTooltip, Legend);

const SbiEducationLoanEmiCal = () => {
  const [loanAmount, setLoanAmount] = useState(5000000);
  const [interestRate, setInterestRate] = useState(7.5);
  const [loanTenure, setLoanTenure] = useState(20);
  const [monthlyEMI, setMonthlyEMI] = useState(0);
  const [totalInterest, setTotalInterest] = useState(0);
  const [totalAmount, setTotalAmount] = useState(0);
  const [amortizationSchedule, setAmortizationSchedule] = useState([]);
  const [activeYear, setActiveYear] = useState(null);

  const calculateEMI = () => {
    const principal = loanAmount;
    const annualInterest = interestRate / 100;
    const monthlyInterest = annualInterest / 12;
    const tenureInMonths = loanTenure * 12;

    const emi =
      (principal *
        monthlyInterest *
        Math.pow(1 + monthlyInterest, tenureInMonths)) /
      (Math.pow(1 + monthlyInterest, tenureInMonths) - 1);

    const totalAmount = emi * tenureInMonths;
    const totalInterest = totalAmount - principal;

    setMonthlyEMI(emi.toFixed(2));
    setTotalInterest(totalInterest.toFixed(2));
    setTotalAmount(totalAmount.toFixed(2));

    const schedule = [];
    let balance = principal;
    // let year = new Date().getFullYear();
    let currentYear = new Date().getFullYear();

    let currentMonth = 6;

    for (let i = 0; i < tenureInMonths; i++) {
      const interestForMonth = balance * monthlyInterest;
      const principalForMonth = emi - interestForMonth;
      balance -= principalForMonth;

      schedule.push({
        // month: i + 1,
        // year: year,
        month: ((currentMonth + i) % 12) + 1, // Ensure month is in range 1-12
        year: currentYear + Math.floor((currentMonth + i) / 12),
        principalPaid: principalForMonth,
        interestPaid: interestForMonth,
        totalPayment: emi,
        balance: balance,
      });

      // if ((i + 1) % 12 === 0) {
      //   year++;
      // }
    }

    setAmortizationSchedule(schedule);
  };

  const handleLoanAmountChange = (e) => {
    const value = e.target.value;
    setLoanAmount(value === "" ? 0 : parseInt(value));
  };

  const handleInterestRateChange = (e) => {
    const value = e.target.value;
    setInterestRate(value === "" ? 0 : parseFloat(value));
  };

  const handleLoanTenureChange = (e) => {
    const value = e.target.value;
    setLoanTenure(value === "" ? 0 : parseInt(value));
  };

  const handleYearToggle = (year) => {
    setActiveYear(activeYear === year ? null : year);
  };

  const data = {
    labels: ["Principal Amount", "Total Interest"],
    datasets: [
      {
        label: "EMI Breakdown",
        data: [loanAmount, totalInterest],
        backgroundColor: ["#36A2EB", "#FF6384"],
      },
    ],
  };
  const categories = [
    { id: 1, name: "Category 1", link: "/" },
    { id: 2, name: "Category 2", link: "/" },
    { id: 3, name: "Category 3", link: "/" },
    { id: 4, name: "Category 4", link: "/" },
    // Add more categories as needed
  ];
  function Icon({ id, open }) {
    return (
      <svg
        xmlns="http://www.w3.org/2000/svg"
        fill="none"
        viewBox="0 0 24 24"
        strokeWidth={2}
        stroke="currentColor"
        className={`${
          id === open ? "rotate-180" : ""
        } h-5 w-5 transition-transform`}
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          d="M19.5 8.25l-7.5 7.5-7.5-7.5"
        />
      </svg>
    );
  }

  const [activeIndex, setActiveIndex] = useState(0);

  const faqs = [
    {
      question:
        "Can I use the EMI calculator before the moratorium period ends?",
      answer:
        "Yes, it’s best to plan your repayment in advance. You can use it to estimate post-study EMI amounts even during your course.",
    },
    {
      question: "Does SBI offer fixed or floating rates on education loans?",
      answer:
        "SBI usually offers floating rates linked to MCLR or EBLR. This means your EMI may change with market fluctuations.",
    },
    {
      question: "Can I prepay my SBI education loan without penalty?",
      answer:
        "Yes, SBI allows prepayments and even full foreclosures with zero charges, helping you save on interest.",
    },
    {
      question: "Will the calculator include interest during the moratorium?",
      answer:
        "No, this tool only calculates EMIs post-moratorium. You need to factor moratorium interest separately.",
    },
    {
      question: "How does the tenure affect my EMI?",
      answer:
        "Longer tenure reduces EMI but increases the total repayment. The calculator lets you compare both scenarios easily.",
    },
    {
      question: "Can I change inputs midway in the calculator?",
      answer:
        "Absolutely, you can modify loan amount, rate, or tenure any number of times to check various outcomes.",
    },
    {
      question: "Is it useful for part-time courses too?",
      answer:
        "Yes, as long as SBI is financing it, the EMI calculator remains relevant for any course type.",
    },
    {
      question: "Can parents or co-borrowers use this tool?",
      answer:
        "Definitely. It's designed for both students and co-borrowers to understand and plan repayment effectively.",
    },
    {
      question: "Does this calculator consider subsidies like CSIS?",
      answer:
        "No, it shows gross EMI. However, you can reduce the loan amount manually based on subsidy estimates.",
    },
    {
      question: "Can I save or print the amortisation schedule?",
      answer:
        "Yes, most calculators including ours allow you to download the schedule for future reference or sharing.",
    },
    {
      question: "What is the minimum loan amount I can check?",
      answer:
        "You can input any amount starting from ₹50,000 up to ₹1 crore to simulate EMIs on the tool.",
    },
    {
      question: "Are top-up education loans covered?",
      answer:
        "Yes, you can use this calculator to check EMIs on top-up or additional education funding as well.",
    },
    {
      question: "Can I use this for international courses?",
      answer:
        "Yes, the calculator is suitable for both domestic and international education loans.",
    },
    {
      question: "Is the EMI calculator mobile-friendly?",
      answer:
        "Yes, TheFinMaster’s EMI tools are optimised for all devices, including smartphones and tablets.",
    },
    {
      question: "Will my actual EMI differ from what’s shown?",
      answer:
        "Slightly. The calculator gives a close estimate. Actual EMIs may vary depending on disbursement mode, timing, and rounding.",
    },
  ];
  const toggleAccordion = (index) => {
    if (activeIndex === index) {
      // If the clicked question is already expanded, collapse it
      setActiveIndex(null);
    } else {
      // If the clicked question is not expanded, expand it
      setActiveIndex(index);
    }
  };
  return (
    <>
      <Helmet>
           <title>SBI Education Loan EMI Calculator - Smart EMI Planning Tool</title>
          <meta  name='description' content='Calculate SBI education loan EMI instantly with our easy-to-use calculator. Plan your education loan repayment efficiently with accurate results.'/>
          <link rel="canonical" href="https://thefinmaster.com/financial-tools-calculators/sbi-education-loan-emi-calculator" />
          <meta name="robots" content="max-snippet:-1, max-image-preview:standard" />
          <meta name="robots" content="max-video-preview:-1" />
        </Helmet>
      <FAQSchema faqs={faqs} />
      <nav className="text-gray-500 text-sm my-4 ml-10" aria-label="Breadcrumb">
        <ol className="list-none p-0 inline-flex">
          <li className="flex items-center">
            <Link to="/" className="text-blue-600 hover:text-blue-800 ">
              Home
            </Link>
            <svg
              className="w-4 h-4 mx-2 text-gray-500"
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path
                fillRule="evenodd"
                d="M7.293 14.707a1 1 0 010-1.414L11.586 9 7.293 4.707a1 1 0 111.414-1.414l5 5a1 1 0 010 1.414l-5 5a1 1 0 01-1.414 0z"
                clipRule="evenodd"
              />
            </svg>
          </li>
          <li className="flex items-center">
            <Link
              to="/financial-tools-calculators"
              className="text-blue-600 hover:text-blue-800"
            >
              Financial Tools Calculators
            </Link>
            <svg
              className="w-4 h-4 mx-2 text-gray-500"
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path
                fillRule="evenodd"
                d="M7.293 14.707a1 1 0 010-1.414L11.586 9 7.293 4.707a1 1 0 111.414-1.414l5 5a1 1 0 010 1.414l-5 5a1 1 0 01-1.414 0z"
                clipRule="evenodd"
              />
            </svg>
          </li>
          <li className="flex items-center">
            <span className="text-gray-500 ">
              SBI Education Loan EMI Calculator
            </span>
          </li>
        </ol>
      </nav>
      <div className="py-4 ">
        <div className="container mx-auto p-4 shadow-xl rounded-md ">
          <h1 className="text-2xl font-bold mb-4">
            SBI Education Loan EMI Calculator
          </h1>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <div className="mb-4">
                <label className="block text-gray-700 font-semibold">
                  Loan Amount
                </label>
                <div className="flex items-center">
                  <input
                    type="number"
                    min="0"
                    value={loanAmount}
                    onChange={handleLoanAmountChange}
                    className="w-1/2 p-2 border rounded mr-2"
                  />
                  <span className="text-gray-700">
                    ₹ {loanAmount.toLocaleString()}
                  </span>
                </div>
                <input
                  type="range"
                  min="0"
                  max="100000000"
                  value={loanAmount}
                  onChange={(e) => setLoanAmount(parseInt(e.target.value))}
                  className="w-full mt-2"
                />
              </div>
              <div className="mb-4">
                <label className="block text-gray-700 font-semibold">
                  Rate of Interest (P.A.)
                </label>
                <div className="flex items-center">
                  <input
                    type="number"
                    min="0"
                    max="100"
                    step="0.1"
                    value={interestRate}
                    onChange={handleInterestRateChange}
                    className="w-1/2 p-2 border rounded mr-2"
                  />
                  <span className="text-gray-700">{interestRate} %</span>
                </div>
                <input
                  type="range"
                  min="0"
                  max="15"
                  step="0.1"
                  value={interestRate}
                  onChange={(e) => setInterestRate(parseFloat(e.target.value))}
                  className="w-full mt-2"
                />
              </div>
              <div className="mb-4">
                <label className="block text-gray-700 font-semibold">
                  Loan Tenure (Years)
                </label>
                <div className="flex items-center">
                  <input
                    type="number"
                    min="0"
                    max="30"
                    value={loanTenure}
                    onChange={handleLoanTenureChange}
                    className="w-1/2 p-2 border rounded mr-2"
                  />
                  <span className="text-gray-700">{loanTenure} Yr</span>
                </div>
                <input
                  type="range"
                  min="0"
                  max="30"
                  value={loanTenure}
                  onChange={(e) => setLoanTenure(parseInt(e.target.value))}
                  className="w-full mt-2"
                />
              </div>
              <button
                onClick={calculateEMI}
                className="bg-blue-500 text-white px-4 py-2 rounded"
              >
                Calculate
              </button>
            </div>
            <div>
              <div className="mb-4">
                <h2 className="text-xl font-bold">Results</h2>
                <p>Monthly EMI: ₹ {parseFloat(monthlyEMI).toLocaleString()}</p>
                <p>Total Principal Amount: ₹ {loanAmount.toLocaleString()}</p>
                <p>
                  Total Interest: ₹ {parseFloat(totalInterest).toLocaleString()}
                </p>
                <p>
                  Total Amount (Principal + Interest): ₹{" "}
                  {parseFloat(totalAmount).toLocaleString()}
                </p>
              </div>
              <div>
                <h2 className="text-xl font-bold">Breakdown</h2>
                <div className="flex justify-center">
                  <Pie
                    data={data}
                    options={{ maintainAspectRatio: false }}
                    width={300}
                    height={300}
                  />
                </div>
              </div>
            </div>
          </div>
          <div className="mt-6">
            <h2 className="text-xl font-bold mb-4">Amortization Schedule</h2>
            {amortizationSchedule.length > 0 && (
              <div className="flex flex-col space-y-4">
                {Array.from(
                  new Set(amortizationSchedule.map((item) => item.year))
                ).map((year, idx) => (
                  <div key={idx} className="mb-4 border rounded-md shadow-md">
                    <div
                      className={`cursor-pointer text-lg font-semibold bg-gray-100 p-4 flex justify-between items-center ${
                        activeYear === year ? "bg-blue-100" : ""
                      }`}
                      onClick={() => handleYearToggle(year)}
                    >
                      <span>{year}</span>
                      <span>{activeYear === year ? "-" : "+"}</span>
                    </div>
                    {activeYear === year && (
                      <div className="overflow-x-auto">
                        <table className="min-w-full bg-white rounded-md shadow-md text-center">
                          <thead>
                            <tr>
                              <th className="py-2 px-4 border-b">Month</th>
                              <th className="py-2 px-4 border-b">
                                Principal Paid
                              </th>
                              <th className="py-2 px-4 border-b">
                                Interest Charged
                              </th>
                              <th className="py-2 px-4 border-b">
                                Total Payment
                              </th>
                              <th className="py-2 px-4 border-b">Balance</th>
                            </tr>
                          </thead>
                          <tbody>
                            {amortizationSchedule
                              .filter((item) => item.year === year)
                              .map((item, idx) => (
                                <tr key={idx}>
                                  <td className="py-2 px-4 border-b">
                                    {new Date(0, item.month - 1).toLocaleString(
                                      "default",
                                      { month: "short" }
                                    )}
                                  </td>
                                  <td className="py-2 px-4 border-b">
                                    ₹{" "}
                                    {item.principalPaid
                                      .toFixed(2)
                                      .toLocaleString()}
                                  </td>
                                  <td className="py-2 px-4 border-b">
                                    ₹{" "}
                                    {item.interestPaid
                                      .toFixed(2)
                                      .toLocaleString()}
                                  </td>
                                  <td className="py-2 px-4 border-b">
                                    ₹{" "}
                                    {item.totalPayment
                                      .toFixed(2)
                                      .toLocaleString()}
                                  </td>
                                  <td className="py-2 px-4 border-b">
                                    ₹ {item.balance.toFixed(2).toLocaleString()}
                                  </td>
                                </tr>
                              ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
        <div className="flex flex-wrap lg:px-6 md:px-6 px-0 py-6">
          <div className="w-full md:w-2/3 p-4 mx-auto">
            <div className="container mx-auto py-4">
              <h1 className="text-xl font-medium bg-blue-100 border-l-4 border-blue-600 pl-4 py-2">
                Plan Your SBI Education Loan with TheFinMaster’s EMI Calculator
              </h1>
              <p className="text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg mt-2">
                Pursuing higher education, especially abroad or in premier
                Indian institutions, often involves substantial financial
                commitments. For many families, education loans become a crucial
                tool to achieve these academic aspirations. However,
                understanding the long-term financial responsibility that comes
                with an education loan is essential. TheFinMaster’s SBI
                Education Loan EMI Calculator is here to simplify this journey.
                It provides precise and instant monthly repayment estimates,
                helping students and parents plan finances with greater clarity
                and control. With flexible inputs, visual amortisation, and
                accurate EMI predictions, this tool ensures you make informed
                decisions at every step of your loan planning. Whether you're
                evaluating different tenures or checking affordability, our
                calculator empowers you to approach student loans with
                confidence.
              </p>
            </div>
            <div className="container mx-auto py-4">
              <h1 className="text-xl font-medium bg-blue-100 border-l-4 border-blue-600 pl-4 py-2">
                What is an Education Loan EMI?
              </h1>
              <p className="mt-4 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
                An Education Loan EMI (Equated Monthly Installment) is a fixed
                monthly amount paid to the bank for repaying the borrowed loan
                over a pre-agreed tenure. Each EMI consists of two parts — the
                interest charged on the outstanding loan balance and a portion
                of the principal amount. In education loans, the repayment
                usually starts after the completion of the course and a
                moratorium period, often 6 months to 1 year. During the study
                and moratorium period, some banks, including SBI, allow
                interest-only payments or even a complete repayment holiday.
                Once the EMI phase starts, the amount remains constant
                throughout the tenure if the interest rate is fixed. For
                floating-rate loans, the EMI may vary with market-linked
                changes.
              </p>
            </div>
            <div className="container mx-auto py-4">
              <h1 className="text-xl font-medium bg-blue-100 border-l-4 border-blue-600 pl-4 py-2">
                What is the SBI Education Loan EMI Calculator?
              </h1>
              <p className="mt-4 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
                The SBI Education Loan EMI Calculator on TheFinMaster is a
                smart, web-based utility that helps borrowers estimate their
                future repayment obligations. By entering key details such as
                the loan amount, interest rate, and tenure, users can instantly
                know their approximate EMI without manually doing complex
                calculations. Key features include:
                <ul className="list-disc pl-6 mt-2">
                  <li>Instant results based on standard EMI formula</li>
                  <li>Option to experiment with different loan terms</li>
                  <li>Breakdown of total interest vs principal repayment</li>
                  <li>
                    Option to visualize the amortisation structure year-by-year
                  </li>
                  <li>
                    Realistic forecasts to plan your post-education finances
                  </li>
                </ul>
                It is ideal for students planning higher studies, parents
                evaluating loan affordability, or graduates preparing for
                repayments.
              </p>
            </div>
            <div className="container mx-auto py-4">
              <h1 className="text-xl font-medium bg-blue-100 border-l-4 border-blue-600 pl-4 py-2">
                Why Use TheFinMaster’s SBI Education Loan EMI Calculator?
              </h1>
              <p className="mt-4 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
                Here’s why TheFinMaster’s EMI calculator is your best financial
                partner when it comes to planning education loans:
                <ul className="list-disc pl-6 mt-2">
                  <li>
                    <strong>Real-Time EMI Projections:</strong> Know your
                    monthly outflow before taking the loan.
                  </li>
                  <li>
                    <strong>Customised Inputs:</strong> Adjust loan amount,
                    rate, and tenure to compare repayment scenarios.
                  </li>
                  <li>
                    <strong>User-Friendly Interface:</strong> Simple layout that
                    works on any device — mobile, tablet, or desktop.
                  </li>
                  <li>
                    <strong>Visual Breakdown:</strong> Pie charts and graphs to
                    help understand interest vs principal ratios.
                  </li>
                  <li>
                    <strong>Secure and Private:</strong> No login, no data
                    storage, no tracking — just pure utility.
                  </li>
                  <li>
                    <strong>Ideal for Students and Parents:</strong> Useful for
                    both decision-making and budgeting.
                  </li>
                  <li>
                    <strong>Time-Saving and Precise:</strong> Eliminates manual
                    errors and gives correct results instantly.
                  </li>
                </ul>
              </p>
            </div>
            <div className="container mx-auto py-4">
              <h1 className="text-xl font-medium bg-blue-100 border-l-4 border-blue-600 pl-4 py-2">
                How Does the SBI Education Loan EMI Calculator Work?
              </h1>
              <p className="mt-4 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
                The EMI is calculated using the standard formula:
              </p>
              <p className="mt-4 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
                EMI = [P × R × (1+R)^N] / [(1+R)^N – 1]
              </p>
              <p className="mt-4 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
                Where:
                <ul className="list-disc pl-6 mt-2">
                  <li>P = Principal loan amount</li>
                  <li>R = Monthly interest rate (Annual rate ÷ 12 ÷ 100)</li>
                  <li>N = Loan tenure in months</li>
                </ul>
              </p>
            
            
            </div>
            <div className="container mx-auto py-4">
                <h1 className="text-xl font-medium bg-blue-100 border-l-4 border-blue-600 pl-4 py-2">
                  What is the Amortisation Schedule?
                </h1>
                <p className="mt-4 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
                  An amortisation schedule is a detailed timeline that outlines
                  every EMI payment, specifying the amount going toward interest
                  and the amount reducing the principal. This schedule evolves
                  over the tenure, with the interest component reducing
                  gradually and the principal component increasing.
                  Understanding this breakdown helps:
                  <ul className="list-disc pl-6 mt-2">
                    <li>Plan early repayments</li>
                    <li>Track how much principal is repaid</li>
                    <li>Estimate total interest savings on prepayments</li>
                    <li>Prepare for balance transfers or refinancing</li>
                  </ul>
                  Sample Amortisation Table (First 12 Months)
                </p>
                <div className="mt-6">
                  <div className="hidden md:block overflow-x-auto">
                    <table className="w-full text-lg text-gray-600 mt-4 border-collapse">
                      <thead>
                        <tr className="bg-gray-100">
                          <th className="border px-4 py-2 text-left">Month</th>
                          <th className="border px-4 py-2 text-left">
                            EMI (₹)
                          </th>
                          <th className="border px-4 py-2 text-left">
                            Interest (₹)
                          </th>
                          <th className="border px-4 py-2 text-left">
                            Principal (₹)
                          </th>
                          <th className="border px-4 py-2 text-left">
                            Outstanding (₹)
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {[
                          ["1", "16,191", "7,500", "8,691", "9,91,309"],
                          ["2", "16,191", "7,435", "8,756", "9,82,553"],
                          ["3", "16,191", "7,369", "8,822", "9,73,731"],
                          ["4", "16,191", "7,303", "8,888", "9,64,843"],
                          ["5", "16,191", "7,236", "8,955", "9,55,888"],
                          ["6", "16,191", "7,169", "9,022", "9,46,866"],
                          ["7", "16,191", "7,101", "9,090", "9,37,776"],
                          ["8", "16,191", "7,033", "9,158", "9,28,618"],
                          ["9", "16,191", "6,964", "9,227", "9,19,391"],
                          ["10", "16,191", "6,895", "9,296", "9,10,095"],
                          ["11", "16,191", "6,825", "9,366", "9,00,729"],
                          ["12", "16,191", "6,755", "9,436", "8,91,293"],
                        ].map(
                          (
                            [month, emi, interest, principal, balance],
                            index
                          ) => (
                            <tr key={index}>
                              <td className="border px-4 py-2">{month}</td>
                              <td className="border px-4 py-2">{emi}</td>
                              <td className="border px-4 py-2">{interest}</td>
                              <td className="border px-4 py-2">{principal}</td>
                              <td className="border px-4 py-2">{balance}</td>
                            </tr>
                          )
                        )}
                      </tbody>
                    </table>
                  </div>
                  <div className="md:hidden space-y-4 mt-4">
                    {[
                      ["1", "16,191", "7,500", "8,691", "9,91,309"],
                      ["2", "16,191", "7,435", "8,756", "9,82,553"],
                      ["3", "16,191", "7,369", "8,822", "9,73,731"],
                      ["4", "16,191", "7,303", "8,888", "9,64,843"],
                      ["5", "16,191", "7,236", "8,955", "9,55,888"],
                      ["6", "16,191", "7,169", "9,022", "9,46,866"],
                      ["7", "16,191", "7,101", "9,090", "9,37,776"],
                      ["8", "16,191", "7,033", "9,158", "9,28,618"],
                      ["9", "16,191", "6,964", "9,227", "9,19,391"],
                      ["10", "16,191", "6,895", "9,296", "9,10,095"],
                      ["11", "16,191", "6,825", "9,366", "9,00,729"],
                      ["12", "16,191", "6,755", "9,436", "8,91,293"],
                    ].map(
                      ([month, emi, interest, principal, balance], index) => (
                        <div
                          key={index}
                          className="bg-white shadow rounded-xl p-4 text-sm text-gray-700 space-y-1 border"
                        >
                          <div>
                            <span className="font-semibold">Month:</span>{" "}
                            {month}
                          </div>
                          <div>
                            <span className="font-semibold">EMI (₹):</span>{" "}
                            {emi}
                          </div>
                          <div>
                            <span className="font-semibold">Interest (₹):</span>{" "}
                            {interest}
                          </div>
                          <div>
                            <span className="font-semibold">
                              Principal (₹):
                            </span>{" "}
                            {principal}
                          </div>
                          <div>
                            <span className="font-semibold">
                              Outstanding (₹):
                            </span>{" "}
                            {balance}
                          </div>
                        </div>
                      )
                    )}
                  </div>
                </div>
              </div>
            <div className="container mx-auto py-4">
              <h1 className="text-xl font-medium bg-blue-100 border-l-4 border-blue-600 pl-4 py-2">
                Factors Affecting Your Education Loan EMI
              </h1>
              <ol className="list-decimal pl-6 mt-2 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
                <li>
                  <strong>Loan Amount</strong>
                  Higher loans increase the EMI proportionately. Only borrow
                  what is required based on your tuition and living expenses.
                </li>
                <li>
                  <strong>Interest Rate</strong>A small change in interest rate
                  can drastically change the EMI. Always compare rates and check
                  for floating vs fixed options.
                </li>
                <li>
                  <strong>Loan Tenure</strong>A longer tenure will lower your
                  EMI but increase the total interest outflow over time.
                </li>
                <li>
                  <strong>Moratorium Period</strong>
                  EMIs often begin after a moratorium. The interest accrued
                  during this time adds to the principal if not serviced.
                </li>
                <li>
                  <strong>Repayment Frequency</strong>
                  SBI may allow monthly, quarterly, or lump sum repayment
                  options post-moratorium which affect the EMI structure.
                </li>
                <li>
                  <strong>Prepayments</strong>
                  Part-prepayments can reduce your loan burden and interest
                  outgo significantly without any pre-closure charges.
                </li>
                <li>
                  <strong>Subsidies or Scholarships</strong>
                  Government subsidy schemes like CSIS can reduce your interest
                  burden during the moratorium period.
                </li>
              </ol>
            </div>
          </div>
          <div className="w-full md:w-1/3 p-4">
            <div className="bg-white shadow-md rounded-lg overflow-hidden">
              <div className="bg-blue-100 px-4 py-2 border-b border-blue-600 ">
                <h2 className="text-lg font-semibold ">Popular Categories</h2>
              </div>
              <div className="p-2 ">
                <ul>
                  <Link to="/financial-tools-calculators/swp-calculator">
                    <li className="py-2  hover:bg-slate-100 border-b border-gray-300">
                      SWP Calculator
                    </li>
                  </Link>
                  <Link to="/financial-tools-calculators/mutul-fund-calculator">
                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">
                      Mutual Fund Calculator
                    </li>
                  </Link>
                  <Link to="/financial-tools-calculators/sukanya-samriddhi-yojana-calculator">
                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">
                      Samriddhi Yojana Calculator
                    </li>
                  </Link>
                  <Link to="/financial-tools-calculators/fd-calculator">
                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">
                      FD Calculator
                    </li>
                  </Link>
                  <Link to="/financial-tools-calculators/rd-calculator">
                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">
                      RD Calculator
                    </li>
                  </Link>
                  <Link to="/financial-tools-calculators/nps-calculator">
                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">
                      NPS Calculator
                    </li>
                  </Link>
                  <Link to="/financial-tools-calculators/hra-exemption-calculator">
                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">
                      HRA Exemption Calculator
                    </li>
                  </Link>
                  <Link to="/financial-tools-calculators/retirement-planning-calculator">
                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">
                      Retirement Planning Calculator
                    </li>
                  </Link>
                  <Link to="/financial-tools-calculators/emi-calculator">
                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">
                      EMI Calculator
                    </li>
                  </Link>
                  <Link to="/financial-tools-calculators/simple-interest-calculator">
                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">
                      Simple Interest Calculator
                    </li>
                  </Link>
                  <Link to="/financial-tools-calculators/compound-interest-calculator">
                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">
                      Compound Interest Calculator
                    </li>
                  </Link>
                  <Link to="/financial-tools-calculators/nsc-calculator">
                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">
                      NSC Calculator
                    </li>
                  </Link>
                  <Link to="/financial-tools-calculators/step-up-sip-calculator">
                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">
                      Step Up Sip Calculator
                    </li>
                  </Link>
                  <Link to="/financial-tools-calculators/income-tax-calculator">
                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">
                      Income Tax Calculator
                    </li>
                  </Link>
                  <Link to="/financial-tools-calculators/home-loan-emi-calculator">
                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">
                      Home Loan Emi Calculator
                    </li>
                  </Link>
                  <Link to="/financial-tools-calculators/car-loan-emi-calculator">
                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">
                      Car Loan Emi Calculator
                    </li>
                  </Link>
                  {/* {categories.map(category => (
       <Link key={category.id} to={`/category/${category.id}`}>
         <li className="py-2 border-b border-gray-300">{category.name}</li>
       </Link>
     ))} */}
                </ul>
              </div>
            </div>
            <div className="bg-white shadow-md rounded-lg overflow-hidden mt-4">
              <div className="bg-blue-100 px-4 py-2 border-b border-blue-600">
                <h2 className="text-lg font-semibold">More Categories</h2>
              </div>
              <div className="p-2">
                <ul>
                  <Link to="/financial-tools-calculators/gratuity-calculator">
                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">
                      Gratuity Calculator
                    </li>
                  </Link>
                  <Link to="/financial-tools-calculators/APY-calculator">
                    <li className="py-2 border- hover:bg-slate-100 border-gray-300">
                      APY Calculator
                    </li>
                  </Link>
                  <Link to="/financial-tools-calculators/cagr-calculator">
                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">
                      CAGR Calculator
                    </li>
                  </Link>
                  <Link to="/financial-tools-calculators/gst-calculator">
                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">
                      GST Calculator
                    </li>
                  </Link>
                  <Link to="/financial-tools-calculators/flat-vs-reducing-rate-calculator">
                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">
                      Flat Vs Reducing Rate Calculator
                    </li>
                  </Link>
                  <Link to="/financial-tools-calculators/equity-margin-calculator">
                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">
                      Equity Margin Calculator
                    </li>
                  </Link>
                  <Link to="/financial-tools-calculators/inflation-calculator">
                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">
                      Inflation Calculator
                    </li>
                  </Link>
                  <Link to="/financial-tools-calculators/salary-calculator">
                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">
                      Salary Calculator
                    </li>
                  </Link>
                  <Link to="/financial-tools-calculators/brokerage-calculator">
                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">
                      Brokerage Calculator
                    </li>
                  </Link>
                  <Link to="/financial-tools-calculators/tds-calculator">
                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">
                      TDS Calculator
                    </li>
                  </Link>
                  {/* {moreCategories.map(category => (
       <Link key={category.id} to={`/category/${category.id}`}>
         <li className="py-2 border-b border-gray-300">{category.name}</li>
       </Link>
     ))} */}
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="max-w-4xl mx-auto lg:px-6 md:px-6 px-2 py-6">
        <h1 className="text-center text-2xl md:text-2xl lg:text-3xl font-bold mb-8">
          Frequently Asked Questions
        </h1>
        <div className="space-y-4">
          {faqs.map((faq, index) => (
            <div
              key={index}
              className="border rounded-lg overflow-hidden shadow-md"
            >
              <button
                onClick={() => toggleAccordion(index)}
                className="w-full p-4 text-left text-lg font-semibold focus:outline-none flex justify-between items-center"
              >
                {faq.question}
                <Icon id={index} open={activeIndex} />
              </button>
              {activeIndex === index && (
                <div className="p-4 bg-gray-100">
                  <p className="text-gray-700">{faq.answer}</p>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </>
  );
};

export default SbiEducationLoanEmiCal;
